import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import { useSelector } from 'react-redux';
import {
    ChevronLeft,
    ShoppingCart,
    Trash2,
    CreditCard,
    Banknote,
    QrCode,
    CheckCircle2,
    RefreshCw
} from 'lucide-react';

const PointOfSale = () => {
    const { slug, eventId } = useParams();
    const navigate = useNavigate();
    const [catalog, setCatalog] = useState([]);
    const [cart, setCart] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [success, setSuccess] = useState(false);
    const { user } = useSelector(state => state.auth);

    useEffect(() => {
        fetchCatalog();
    }, [eventId]);

    const fetchCatalog = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`/${slug}/api/events/${eventId}/pos-catalog`);
            setCatalog(data);
            setError(null);
        } catch (err) {
            console.error('Error fetching catalog:', err);
            setError('Error al cargar el catálogo de la barra.');
        } finally {
            setLoading(false);
        }
    };

    const addToCart = (product) => {
        if (product.currentQuantity <= 0) return;

        setCart(prev => {
            const existing = prev.find(item => item.productId === product.productId);
            if (existing) {
                if (existing.quantity >= product.currentQuantity) return prev; // Límite de stock
                return prev.map(item =>
                    item.productId === product.productId
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            }
            return [...prev, { ...product, quantity: 1 }];
        });
    };

    const removeFromCart = (productId) => {
        setCart(prev => prev.filter(item => item.productId !== productId));
    };

    const updateQuantity = (productId, delta) => {
        setCart(prev => prev.map(item => {
            if (item.productId === productId) {
                const product = catalog.find(p => p.productId === productId);
                const newQ = item.quantity + delta;
                if (newQ > 0 && newQ <= product.currentQuantity) {
                    return { ...item, quantity: newQ };
                }
            }
            return item;
        }));
    };

    const totalCart = cart.reduce((acc, item) => acc + (item.price * item.quantity), 0);

    const processSale = async (paymentMethod) => {
        if (cart.length === 0) return;

        setProcessing(true);
        try {
            const items = cart.map(item => ({
                productId: item.productId,
                quantity: item.quantity
            }));

            await axios.post(`/${slug}/api/events/${eventId}/sales`, {
                items,
                paymentMethod
            });

            setSuccess(true);
            setCart([]);
            fetchCatalog(); // Actualizar stock

            setTimeout(() => {
                setSuccess(false);
            }, 2000);
        } catch (err) {
            console.error('Error processing sale:', err);
            alert(err.response?.data?.error || 'Error al procesar la venta');
        } finally {
            setProcessing(false);
        }
    };

    if (loading) return (
        <div className="min-h-screen bg-[#080808] flex items-center justify-center">
            <div className="w-12 h-12 border-4 border-primary-orange/20 border-t-primary-orange rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="min-h-screen bg-[#080808] text-white flex flex-col md:flex-row font-sans selection:bg-primary-orange/30">

            {/* 📱 LEFT SIDE: CATALOG Grid */}
            <div className="flex-1 flex flex-col h-screen overflow-hidden">
                {/* Header */}
                <div className="h-16 border-b border-white/5 flex items-center justify-between px-6 bg-[#0D0D0D] shrink-0">
                    <button
                        onClick={() => navigate(`/${slug}/admin/events`)}
                        className="flex items-center gap-2 text-white/40 hover:text-white transition-colors"
                    >
                        <ChevronLeft size={20} />
                        <span className="font-bold text-sm tracking-wide">Volver</span>
                    </button>
                    <div className="flex items-center gap-3">
                        <h1 className="font-black text-xl tracking-tighter">PUNTO DE VENTA</h1>
                        <span className="px-2 py-0.5 bg-primary-orange/20 text-primary-orange text-[10px] font-black uppercase tracking-widest rounded-md">LIVE</span>
                    </div>
                    <button onClick={fetchCatalog} className="text-white/40 hover:text-white">
                        <RefreshCw size={20} />
                    </button>
                </div>

                {/* Product Grid */}
                <div className="flex-1 overflow-y-auto p-6 scroll-smooth">
                    {error && (
                        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl mb-6 font-medium">
                            {error}
                        </div>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                        {catalog.map(product => {
                            const inCart = cart.find(c => c.productId === product.productId);
                            const isOut = product.currentQuantity <= 0;

                            return (
                                <button
                                    key={product.productId}
                                    onClick={() => addToCart(product)}
                                    disabled={isOut}
                                    className={`
                    flex flex-col h-full text-left p-4 rounded-3xl border transition-all active:scale-95
                    ${isOut
                                            ? 'bg-red-500/5 border-red-500/10 opacity-50 cursor-not-allowed'
                                            : inCart
                                                ? 'bg-primary-orange/10 border-primary-orange/50 shadow-[0_0_20px_-5px_rgba(255,107,0,0.2)]'
                                                : 'bg-[#121212] border-white/5 hover:border-white/20 hover:bg-[#1A1A1A]'
                                        }
                  `}
                                >
                                    <div className="flex-1 mb-4">
                                        <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isOut ? 'text-red-400/50' : 'text-primary-orange'}`}>
                                            {product.category}
                                        </p>
                                        <h3 className="text-lg font-black leading-tight line-clamp-2">{product.name}</h3>
                                    </div>
                                    <div className="flex items-end justify-between w-full">
                                        <p className="text-xl font-black">${product.price.toLocaleString()}</p>
                                        <div className="text-right">
                                            <p className={`text-[10px] font-bold uppercase ${isOut ? 'text-red-400' : 'text-white/40'}`}>
                                                {isOut ? 'AGOTADO' : `Stock: ${product.currentQuantity}`}
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* 🛒 RIGHT SIDE: CART PANEL */}
            <div className="w-full md:w-[400px] bg-[#121212] border-l border-white/5 flex flex-col h-[50vh] md:h-screen shrink-0 relative">
                {success && (
                    <div className="absolute inset-0 z-50 bg-green-500 flex flex-col items-center justify-center animate-in fade-in zoom-in duration-200">
                        <CheckCircle2 size={64} className="text-black mb-4" />
                        <h2 className="text-3xl font-black text-black">¡COBRADO!</h2>
                    </div>
                )}

                {/* Cart Header */}
                <div className="h-16 border-b border-white/5 flex items-center justify-between px-6 bg-[#0D0D0D]">
                    <h2 className="font-black text-lg flex items-center gap-2">
                        <ShoppingCart className="text-primary-orange" size={20} />
                        CUENTA ACTUAL
                    </h2>
                    {cart.length > 0 && (
                        <button onClick={() => setCart([])} className="text-white/40 hover:text-red-400 text-xs font-bold uppercase tracking-wider">
                            Vaciar
                        </button>
                    )}
                </div>

                {/* Cart Items */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {cart.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-white/20">
                            <ShoppingCart size={48} className="mb-4 opacity-50" />
                            <p className="font-medium text-sm">Tocá los productos para agregar</p>
                        </div>
                    ) : (
                        cart.map(item => (
                            <div key={item.productId} className="bg-white/5 border border-white/5 rounded-2xl p-3 flex items-center justify-between animate-fade-in-up">
                                <div className="flex-1 min-w-0 pr-2">
                                    <h4 className="font-black text-sm truncate">{item.name}</h4>
                                    <p className="text-primary-orange font-bold text-sm">${item.price.toLocaleString()}</p>
                                </div>

                                <div className="flex items-center gap-3">
                                    <div className="flex items-center bg-black/40 rounded-xl border border-white/5 p-1">
                                        <button
                                            onClick={() => updateQuantity(item.productId, -1)}
                                            className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white"
                                        >
                                            −
                                        </button>
                                        <span className="w-6 text-center font-black">{item.quantity}</span>
                                        <button
                                            onClick={() => updateQuantity(item.productId, 1)}
                                            className="w-8 h-8 flex items-center justify-center text-primary-orange"
                                        >
                                            +
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => removeFromCart(item.productId)}
                                        className="w-10 h-10 flex items-center justify-center bg-red-500/10 text-red-500 rounded-xl hover:bg-red-500/20 transition-colors"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Checkout Footer */}
                <div className="p-6 bg-[#0D0D0D] border-t border-white/5">
                    <div className="flex items-end justify-between mb-6">
                        <span className="text-white/40 font-bold uppercase tracking-widest text-sm">Total</span>
                        <span className="text-4xl font-black text-primary-orange">${totalCart.toLocaleString()}</span>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                        <button
                            disabled={cart.length === 0 || processing}
                            onClick={() => processSale('cash')}
                            className="flex flex-col items-center justify-center gap-2 p-4 bg-green-500/10 text-green-500 border border-green-500/20 rounded-2xl hover:bg-green-500/20 transition-all disabled:opacity-50 disabled:grayscale"
                        >
                            <Banknote size={24} />
                            <span className="text-[10px] font-black uppercase tracking-widest">Efectivo</span>
                        </button>
                        <button
                            disabled={cart.length === 0 || processing}
                            onClick={() => processSale('mp')}
                            className="flex flex-col items-center justify-center gap-2 p-4 bg-blue-500/10 text-blue-500 border border-blue-500/20 rounded-2xl hover:bg-blue-500/20 transition-all disabled:opacity-50 disabled:grayscale"
                        >
                            <QrCode size={24} />
                            <span className="text-[10px] font-black uppercase tracking-widest">Mercado Pago</span>
                        </button>
                        <button
                            disabled={cart.length === 0 || processing}
                            onClick={() => processSale('card')}
                            className="flex flex-col items-center justify-center gap-2 p-4 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded-2xl hover:bg-purple-500/20 transition-all disabled:opacity-50 disabled:grayscale"
                        >
                            <CreditCard size={24} />
                            <span className="text-[10px] font-black uppercase tracking-widest">Tarjeta</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PointOfSale;
